import { BsXCircle } from '@react-icons/all-files/bs/BsXCircle';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind, deepStringFind,
    DisplayFilterContainer,
    DisplayFilterInput, IDisplayFilterOptions, IFilterOption,
    IInputMultiSelectFiltersMeta,
    inject,
    InputMultiSelectFilters,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';
import { BsDashCircle } from '@react-icons/all-files/bs/BsDashCircle';
import { Button } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { displayFilters } from './displayFilters';
import { DisplayListViewModel } from './DisplayListViewModel';
import {DisplayFiltersStore} from "./DisplayFiltersStore";
import {action, computed} from "mobx";

export interface IDisplayFilterProps {
    readonly vm: DisplayListViewModel;
    readonly renderBefore?: React.ReactNode;
    readonly renderAfter?: React.ReactNode;
    readonly isInForm?: boolean;
    readonly className?: string;
    readonly inputClass?: string;
    readonly hideInput?: boolean;
    readonly hideClearSort?: boolean;
}

@autoinject
@observer
export class DisplayFilter extends React.Component<IDisplayFilterProps> {
    public async componentDidMount() {
        try {
            await this.props.vm.loadAllOptionsAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        }
    }

    public render() {
        const { renderBefore, renderAfter, isInForm, vm, className, inputClass } = this.props;
        const { clearAll, onSearchChange, pushFilter, removeLastFilter } = vm;

        return (
            <div className={`${className} display-filter-wrapper`}>
                {renderBefore}

                <DisplayFilterContainer
                    className={inputClass}
                    hide={this.props.hideInput}
                    renderAfter={this.renderAfter()}
                >
                    <InputMultiSelectFilters vm={vm} filtersMeta={vm.filters} isInForm={isInForm} />
                    
                    <DisplayFilterInput
                        ref={(ref) => (this.displayFilterInputRef = ref)}
                        filters={vm.filters}
                        filtersMeta={displayFilters}
                        filter={vm.displayFilter}
                        isInForm={isInForm}
                        value={vm.displayFilter.textSearch ?? ''}
                        filterOptions={this.filterOptions}
                        filterOpen={vm.filterOpen}
                        onBackspace={removeLastFilter}
                        onChange={onSearchChange}
                        onCheck={this.onCheck}
                        onAddFilter={pushFilter}
                        isChecked={this.isChecked} // Pass the isChecked method
                    />
                </DisplayFilterContainer>

                {renderAfter}
            </div>
        );
    }
    
    @bind
    @action
    private onCheck(option: IFilterOption, meta: IInputMultiSelectFiltersMeta<any>){
        const filter = this.props.vm.displayFilter;
        const checked = this.isChecked(option, meta);

        if (checked) {
            if (meta.id == 'accounts') {
                filter.accounts = filter.accounts ? filter.accounts.filter(a => a.accountNumber !== option.value.accountNumber) : [];
            }
            if (meta.id == 'contracts') {
                filter.contracts = filter.contracts ? filter.contracts.filter(c => c.serviceCO !== option.value.serviceCO) : [];
            }
            if (meta.id == 'nocServers') {
                filter.nocServers = filter.nocServers ? filter.nocServers.filter(n => n !== option.value) : [];
            }
            if (meta.id == 'cities') {
                filter.cities = filter.cities ? filter.cities.filter(c => c !== option.value) : [];
            }
            if (meta.id == 'partDefinitions') {
                filter.partDefinitions = filter.partDefinitions ? filter.partDefinitions.filter(p => p.partNumber !== option.value.partNumber) : [];
            }
            if (meta.id == 'stateOrProvinces') {
                filter.stateOrProvinces = filter.stateOrProvinces ? filter.stateOrProvinces.filter(s => s !== option.value) : [];
            }
            if (meta.id == 'countries') {
                filter.countries = filter.countries ? filter.countries.filter(c => c !== option.value) : [];
            }
            if (meta.id == 'displayGroups') {
                filter.displayGroups = filter.displayGroups ? filter.displayGroups.filter(d => d !== option.value) : [];
            }
            if (meta.id == 'currentSoftwareVersions') {
                filter.currentSoftwareVersions = filter.currentSoftwareVersions ? filter.currentSoftwareVersions.filter(v => v !== option.value) : [];
            }
        } else {
            if (meta.id == 'accounts') {
                filter.accounts ? filter.accounts.push(option.value) : filter.accounts = [option.value];
            }
            if (meta.id == 'contracts') {
                filter.contracts ? filter.contracts.push(option.value) : filter.contracts = [option.value];
            }
            if (meta.id == 'nocServers') {
                filter.nocServers ? filter.nocServers.push(option.value) : filter.nocServers = [option.value];
            }
            if (meta.id == 'cities') {
                filter.cities ? filter.cities.push(option.value) : filter.cities = [option.value];
            }
            if (meta.id == 'partDefinitions') {
                filter.partDefinitions ? filter.partDefinitions.push(option.value) : filter.partDefinitions = [option.value];
            }
            if (meta.id == 'stateOrProvinces') {
                filter.stateOrProvinces ? filter.stateOrProvinces.push(option.value) : filter.stateOrProvinces = [option.value];
            }
            if (meta.id == 'countries') {
                filter.countries ? filter.countries.push(option.value) : filter.countries = [option.value];
            }
            if (meta.id == 'displayGroups') {
                filter.displayGroups ? filter.displayGroups.push(option.value) : filter.displayGroups = [option.value];
            }
            if (meta.id == 'currentSoftwareVersions') {
                filter.currentSoftwareVersions ? filter.currentSoftwareVersions.push(option.value) : filter.currentSoftwareVersions = [option.value];
            }
        }
    }

    @bind
    private isChecked(option: IFilterOption, meta: IInputMultiSelectFiltersMeta<any>) {
        let filter = this.props.vm.displayFilter;

        if (meta.id == 'accounts' && filter.accounts) {
            return !!filter.accounts.find(a => a.accountNumber == option.value.accountNumber);
        }
        if (meta.id == 'contracts' && filter.contracts) {
            return !!filter.contracts.find(a => a.serviceCO == option.value.serviceCO);
        }
        if (meta.id == 'nocServers' && filter.nocServers) {
            return filter.nocServers.includes(option.value);
        }
        if (meta.id == 'cities' && filter.cities) {
            return filter.cities.includes(option.value);
        }
        if (meta.id == 'partDefinitions' && filter.partDefinitions) {
            return !!filter.partDefinitions.find(p => p.partNumber == option.value.partNumber);
        }
        if (meta.id == 'stateOrProvinces' && filter.stateOrProvinces) {
            return filter.stateOrProvinces.includes(option.value);
        }
        if (meta.id == 'countries' && filter.countries) {
            return filter.countries.includes(option.value);
        }
        if (meta.id == 'displayGroups' && filter.displayGroups) {
            return filter.displayGroups.includes(option.value);
        }
        if (meta.id == 'currentSoftwareVersions' && filter.currentSoftwareVersions) {
            return filter.currentSoftwareVersions.includes(option.value);
        }

        return false;
    }

    @computed
    private get filterOptions() {
        let allFilterOptions = this.displayFiltersStore.all;
        let filtersMeta = displayFilters;
        let options: IDisplayFilterOptions[] = [];
        if(allFilterOptions) {
            options.push({
                name: 'accounts',
                options: allFilterOptions.accounts.map(a => ({ title: a.accountName, value: a })),
                meta: filtersMeta.find(m => m.id == 'accounts')
            });
            options.push({
                name: 'contracts',
                options: allFilterOptions.contracts.map(c => ({ title: c.serviceCO, value: c })),
                meta: filtersMeta.find(m => m.id == 'contracts')
            });
            options.push({
                name: 'nocServers',
                options: allFilterOptions.nocServers.map(n => ({ title: n, value: n })),
                meta: filtersMeta.find(m => m.id == 'nocServers')
            });
            options.push({
                name: 'partDefinitions',
                options: allFilterOptions.partDefinitions.map(p => ({ title: p.partNumber, value: p })),
                meta: filtersMeta.find(m => m.id == 'partDefinitions')
            });
            options.push({
                name: 'cities',
                options: allFilterOptions.cities.map(c => ({ title: c, value: c })),
                meta: filtersMeta.find(m => m.id == 'cities')
            });
            options.push({
                name: 'stateOrProvinces',
                options: allFilterOptions.stateOrProvinces.map(s => ({ title: s, value: s })),
                meta: filtersMeta.find(m => m.id == 'stateOrProvinces')
            });
            options.push({
                name: 'countries',
                options: allFilterOptions.countries.map(c => ({ title: c, value: c })),
                meta: filtersMeta.find(m => m.id == 'countries')
            });
            options.push({
                name: 'displayGroups',
                options: allFilterOptions.displayGroups.map(d => ({ title: d, value: d })),
                meta: filtersMeta.find(m => m.id == 'displayGroups')
            });
            options.push({
                name: 'currentSoftwareVersions',
                options: allFilterOptions.currentSoftwareVersions.map(v => ({ title: v, value: v })),
                meta: filtersMeta.find(m => m.id == 'currentSoftwareVersions')
            });
        }
        
        return options;
    }

    @bind
    private renderAfter() {
        const { clearAll, sort } = this.props.vm;

        return (
            <>
                {!this.props.hideClearSort && sort.length > 0 && (
                    <Button onClick={this.props.vm.clearSort} className="btn btn-link clear-sort-btn" color="link">
                        <BsDashCircle style={{ fontSize: 18 }} className=" f-12 p-0" color="red" />
                    </Button>
                )}
                <Button onClick={this.clearAll} className="btn btn-link clear-all-btn" color="link">
                    <BsXCircle color="red" />
                </Button>
            </>
        );
    }
    
    @bind
    @action
    private clearAll() {
        // Existing clearAll logic
        this.props.vm.clearAll();

        // Clear searchInputValue in DisplayFilterInput
        if (this.displayFilterInputRef) {
            this.displayFilterInputRef.clearSearchInput();
        }
    }

    private displayFilterInputRef: DisplayFilterInput | null = null;
    @inject
    private appStore!: AppStore;
    @inject
    private readonly displayFiltersStore!: DisplayFiltersStore;
}
